import { 
  AttachmentTypeEnum, 
  AuctionTypeEnum, 
  ExternalAttachmentInterface, 
  ListPaginationInterface, 
  PublicProfileTypeEnum, 
  ReactionUsageContextTypeEnum, 
  ThumbnailAttachmentTypeEnum, 
  UploadedAttachmentInterface 
} from '@on-arte/core-types';
import { 
  IconName, 
  InfinitePaginationDetails, 
  ItemsMosaic, 
  ItemsMosaicTheme, 
  LoadingSpinner, 
  UseLogger, 
  UseNotifications, 
  UseRedirect, 
  UseState, 
  getPathWithParams, 
  useInfinitePagination, 
  useLogger, 
  useNotifications, 
  useRedirect,
} from '@on-arte/ui';
import React, { useEffect, useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { QueryClient, useQuery, useQueryClient } from 'react-query';

import { getPublicProfileDetails, getPublicProfileItems } from '@onArte/frontend/api/requests';
import { FrontendResponseCode, QueryKey } from '@onArte/frontend/enums';
import { useRouteInfo, useReactions } from '@onArte/frontend/hooks';
import { RoutingState, UseReactions, ArtworkMosaicItemDetailsWithItemId } from '@onArte/frontend/interfaces';
import { FrontendApiError } from '@onArte/frontend/models';
import { emptyRequest, getImageThumbnail } from '@onArte/frontend/utils';
import { ssrQueryKey } from '@onArte/shared/constants';
import { RouteNameEnum } from '@onArte/shared/enums';
import { ItemWithAuctionDetails, PublicProfileDetails } from '@onArte/shared/interfaces';
import { buildArtistTitle, getRouteDetailsByName } from '@onArte/shared/utils';

import { BlogBoxesSliderSection } from '../blogBoxesSliderSection/blogBoxesSliderSection.component';

import { 
  BioArtworkDescription, 
  Container, 
  MosaicWrapper,
  DetailsContainer, 
  DetailsWrapper,
  PhotoBackgroundContainer, 
  StyledArtistTitle, 
  StyledArtworkDescription,
} from './baseViewWithBio.styled';
import { BaseViewWithBioProps } from './baseViewWithBio.types';

export const BaseViewWithBio: React.FC<BaseViewWithBioProps> = (props: BaseViewWithBioProps): JSX.Element => {
  const { t }: TransProps<never> = useTranslation();
  const { type, route }: BaseViewWithBioProps = props;
  const { logger }: UseLogger = useLogger();
  const { redirect }: UseRedirect = useRedirect();
  const { addToast }: UseNotifications = useNotifications();
  const { elementParamsFromRouting, currentRouteObject }: RoutingState = useRouteInfo();
  const { 
    hasMoreItems, 
    fetchMoreData, 
    initialItemsCount, 
    paginationOffset, 
    setMaxItems 
  }: InfinitePaginationDetails = useInfinitePagination(20);
  const { 
    userFavouriteManufacturersIds, 
    isFavourite, 
    changeLikeState,
    favoritesReadyState,
    userFavouriteItemsIds
  }: UseReactions = useReactions();
  // Dane profilu zasiane przez serwer (ssrPrefetch.helper.ts) pod tym samym kluczem,
  // którego używa poniższe useQuery. Bierzemy je jako stan początkowy, bo `onSuccess`
  // przy renderowaniu serwerowym się nie wykonuje, a bez `details` widok nie renderuje
  // niczego.
  const queryClient: QueryClient = useQueryClient();
  const ssrProfileDetails: PublicProfileDetails | undefined = queryClient
    .getQueryData<PublicProfileDetails>([QueryKey.PublicProfileDetails, elementParamsFromRouting.id]);

  const [details, setDetails]: UseState<PublicProfileDetails | null>
    = useState<PublicProfileDetails | null>(ssrProfileDetails ?? null);
  // Stan `null`, nie pusta tablica: pusta tablica nie odróżnia „jeszcze nie wczytane"
  // od „wczytane i puste". Gdy praca zniknie między odpowiedzią serwera a zapytaniem
  // przeglądarki, widok zostawałby przy danych z serwera na stałe.
  const [mosaicItems, setMosaicItems]: UseState<ArtworkMosaicItemDetailsWithItemId[] | null>
    = useState<ArtworkMosaicItemDetailsWithItemId[] | null>(null);

  const mapItemToMosaic: (item: ItemWithAuctionDetails) => ArtworkMosaicItemDetailsWithItemId = (
    item: ItemWithAuctionDetails
  ): ArtworkMosaicItemDetailsWithItemId => {
    const isItemLiked: boolean = isFavourite(item.itemId, ReactionUsageContextTypeEnum.Item);

    return {
      itemId: item.itemId,
      image: getImageThumbnail(item.attachment, ThumbnailAttachmentTypeEnum.Size_340xAuto),
      action: !item.auctionId
        ? (): void => addToast({ content: t('onarte.common.mosaicItems.noAuction') })
        : undefined,
      internalPath: item.auctionId
        ? item.route?.prettyUrl
          ?? getPathWithParams(getRouteDetailsByName(RouteNameEnum.ArtworkDetails)?.url ?? '/', { id: item.auctionId })
        : undefined,
      boxTitleDetails: {
        itemName: item.name,
        author: item.manufacturer.name,
        price: item.auctionType === AuctionTypeEnum.PriceProposal ? undefined : item.price,
        bottomButtonLabel: item.auctionType === AuctionTypeEnum.PriceProposal ? t('onarte.common.priceProposal') : undefined,
        itemDescriptiveAttributes: [t(item.label)],
      },
      isLiked: isItemLiked,
      onLikeClick: (): void => changeLikeState(item.itemId, isItemLiked, ReactionUsageContextTypeEnum.Item)
    };
  };

  // Prace artysty: klucz zapytania niżej zawiera `favoritesReadyState`, przełączane
  // dopiero w efekcie, więc serwer sieje je pod stabilnym kluczem SSR. Dane służą
  // wyłącznie pierwszemu renderowaniu — gdy zapytanie klienckie wróci i wypełni stan,
  // przestają być używane (stan doładowuje kolejne strony, te dane nie).
  //
  // Identyfikator profilu w kluczu jest konieczny: pamięć podręczna przeniesiona
  // z serwera żyje w przeglądarce dalej, więc bez niego strona kolejnego artysty
  // czytałaby prace poprzedniego.
  const ssrMosaicItems: ArtworkMosaicItemDetailsWithItemId[] = useMemo(
    (): ArtworkMosaicItemDetailsWithItemId[] => {
      const seeded: ListPaginationInterface<ItemWithAuctionDetails> | undefined = queryClient
        .getQueryData<ListPaginationInterface<ItemWithAuctionDetails>>(
          ssrQueryKey(`${QueryKey.PublicProfileItems}-${elementParamsFromRouting.id}`)
        );

      return seeded ? seeded.list.map(mapItemToMosaic) : [];
    },
    // jw. — bez zależności od polubień kafel już polubionej pracy pokazywałby serce puste,
    // a kliknięcie próbowałoby ją DODAĆ do ulubionych zamiast usunąć. Identyfikator profilu
    // jest w kluczu odczytu, więc musi być i tutaj: przy przejściu wprost z jednego artysty
    // na drugiego React zachowuje ten sam komponent i bez tego zostałyby prace pierwszego.
    [userFavouriteItemsIds, elementParamsFromRouting.id]
  );
  const visibleMosaicItems: ArtworkMosaicItemDetailsWithItemId[] = mosaicItems ?? ssrMosaicItems;

  useQuery(
    [QueryKey.PublicProfileDetails, elementParamsFromRouting.id],
    (): Promise<PublicProfileDetails | void> => elementParamsFromRouting.id && currentRouteObject?.name === route
      ? getPublicProfileDetails(elementParamsFromRouting.id)
      : emptyRequest(),
    {
      onSuccess: (data: PublicProfileDetails | void): void => {
        // jw. — bez adresu w stanie routingu zapytanie zwraca emptyRequest()
        if (!data) {
          return;
        }

        if (data.type !== type) {
          redirect(getRouteDetailsByName(RouteNameEnum.NotFound)?.url ?? '/');
        }
        setDetails(data);
      },
      onError: (error: FrontendApiError): void => {
        logger(QueryKey.PublicProfileDetails, error);
        if (error.responseCode === FrontendResponseCode.NotFound) {
          redirect(getRouteDetailsByName(RouteNameEnum.NotFound)?.url ?? '/');
          return;
        } else {
          addToast({ content: t(error.message) });
        }
      }
    }
  );

  useQuery(
    [QueryKey.PublicProfileItems, paginationOffset, elementParamsFromRouting.id, favoritesReadyState],
    (): Promise<ListPaginationInterface<ItemWithAuctionDetails> | void> => (
      elementParamsFromRouting.id && currentRouteObject?.name === route && favoritesReadyState
    )
      ? getPublicProfileItems({ limit: initialItemsCount, offset: paginationOffset }, elementParamsFromRouting.id) 
      : emptyRequest()
    ,
    {
      onSuccess: (data: ListPaginationInterface<ItemWithAuctionDetails> | void): void => {
        // Dopóki nie ustali się stan polubień, funkcja pobierająca zwraca emptyRequest(),
        // czyli `undefined` — a onSuccess i tak zostaje wywołane. Bez tego warunku leci
        // „Cannot read properties of undefined (reading 'amount')", ten sam błąd
        // co na listingu dzieł. Występuje dziś na produkcji.
        if (!data) {
          return;
        }

        setMaxItems(data.amount);
        setMosaicItems([...(mosaicItems ?? []), ...data.list.map(mapItemToMosaic)]);
      },
      onError: (error: FrontendApiError): void => logger(QueryKey.PublicProfileItems, error)
    }
  );

  // Tytuł ustawia widok, bo `AppWrapper` celowo go nie dotyka na trasie artysty —
  // patrz `routesManagingOwnTitle`. Warunek na `route` jest konieczny, bo ten sam
  // komponent obsługuje też profile kolekcjonerów, których szablon z umowy nie obejmuje.
  useEffect(
    (): void => {
      if (!details || route !== RouteNameEnum.ArtistDetails) {
        return;
      }

      document.title = buildArtistTitle(details.name);
    },
    [details, route]
  );

  useEffect(
    (): void => {
      // Przed pierwszą odpowiedzią klienta nie ma czego przeliczać, a zapis pustej
      // tablicy odciąłby dane przysłane przez serwer.
      if (!mosaicItems) {
        return;
      }

      setMosaicItems(mosaicItems.map((item: ArtworkMosaicItemDetailsWithItemId) => {
        const isItemLiked: boolean = !!userFavouriteItemsIds.find((value: string) => value === item.itemId);
        return { 
          ...item, 
          isLiked: isItemLiked,
          onLikeClick: (): void => changeLikeState(item.itemId, isItemLiked, ReactionUsageContextTypeEnum.Item)
        };
      }));
    },
    [userFavouriteItemsIds]
  );

  const getFilteredAttachments: (
    photoType: AttachmentTypeEnum
  ) => (ExternalAttachmentInterface | UploadedAttachmentInterface)[] = (
    photoType: AttachmentTypeEnum
  ): (ExternalAttachmentInterface | UploadedAttachmentInterface)[] => {
    if (!details) {
      return [];
    }

    return details.attachments.filter((
      img: ExternalAttachmentInterface | UploadedAttachmentInterface
    ): boolean => img.type === photoType);
  };

  const coverPhoto: string = useMemo(
    (): string => {
      const filteredAttachments: (ExternalAttachmentInterface | UploadedAttachmentInterface)[] = getFilteredAttachments(
        AttachmentTypeEnum.PublicProfileCoverPhoto
      );

      return filteredAttachments.length
        ? filteredAttachments[0].path
        : '';
    },
    [details]
  );

  const avatarPhoto: string = useMemo(
    (): string => {
      const filteredAttachments: (ExternalAttachmentInterface | UploadedAttachmentInterface)[] = getFilteredAttachments(
        AttachmentTypeEnum.PublicProfileAvatar
      );

      return filteredAttachments.length
        ? getImageThumbnail(filteredAttachments[0], ThumbnailAttachmentTypeEnum.Size_340xAuto)
        : '';
    },
    [details]
  );

  const galleryPhotos: string[] = useMemo(
    (): string[] => {
      const filteredAttachments: (ExternalAttachmentInterface | UploadedAttachmentInterface)[] = getFilteredAttachments(
        AttachmentTypeEnum.PublicProfileGalleryPhoto
      );

      return filteredAttachments
        .map((attachment: ExternalAttachmentInterface | UploadedAttachmentInterface): string => attachment.path)
        .filter(Boolean);
    },
    [details]
  );

  const isProfileManufacturer: boolean = useMemo(
    (): boolean => details?.type === PublicProfileTypeEnum.Manufacturer,
    [details?.type]
  );

  const isManufacturerFavourite: boolean = useMemo(
    (): boolean => isFavourite(details?.relationId ?? '', ReactionUsageContextTypeEnum.Manufacturer),
    [details?.relationId, userFavouriteManufacturersIds.length]
  );

  const changeManufacturerLikeState: () => void = (): void => {
    if (!details?.relationId) {
      return;
    }

    changeLikeState(details.relationId, isManufacturerFavourite, ReactionUsageContextTypeEnum.Manufacturer);
  };

  return (
    <Container>
      {details && (
        <DetailsWrapper>
          {!!coverPhoto && <PhotoBackgroundContainer $imgUrl={coverPhoto} />}
          <DetailsContainer>
            <StyledArtistTitle 
              title={details.name} 
              subtitle={t(`onarte.website.baseViewWithBio.${details.type}.subtitle`)}
              image={avatarPhoto}
              buttonLabel={
                isProfileManufacturer && !!details.relationId
                  ? t(isManufacturerFavourite
                    ? 'onarte.website.baseViewWithBio.button.unfollow'
                    : 'onarte.website.baseViewWithBio.button.follow')
                  : undefined
              }
              buttonIcon={
                isProfileManufacturer && !!details.relationId
                  ? (isManufacturerFavourite
                    ? IconName.FavouriteFilled
                    : IconName.Favourite)
                  : undefined
              }
              buttonAction={
                isProfileManufacturer && !!details.relationId
                  ? changeManufacturerLikeState
                  : undefined
              }
            />
            <StyledArtworkDescription
              title={t('onarte.website.baseViewWithBio.about.title')}
              description={details.description}
              // TODO: temporary hidden - fix when BE will be ready
              // badges={[{
              //   label: 'Selekcja on*arte',
              //   icon: IconName.Collector
              // }]}
            />
          </DetailsContainer>
          {!!visibleMosaicItems.length && (
            <InfiniteScroll
              dataLength={visibleMosaicItems.length}
              next={fetchMoreData}
              hasMore={hasMoreItems}
              loader={
                <LoadingSpinner />
              }
              endMessage={false}
            >
              <MosaicWrapper>
                <ItemsMosaic 
                  items={visibleMosaicItems}
                  theme={ItemsMosaicTheme.Mosaic}
                />
              </MosaicWrapper>
            </InfiniteScroll>
          )}
          {!!details.bio && (
            <BioArtworkDescription 
              title={t('onarte.website.baseViewWithBio.bio.title')}
              description={details.bio}
              images={galleryPhotos}
              hideImagesOnError
            />
          )}
          {!!details.blogPosts.length && <BlogBoxesSliderSection posts={details.blogPosts} withTopBar />}
        </DetailsWrapper>
      )}
    </Container>
  );
};
